import { MailOutlined, ShareAltOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Flex, Typography, message } from "antd";
import React from "react";
import styles from "./template-card.module.scss";

interface TemplateCardProps {
  index: number;
  title: string;
  description: string;
  slug: string;
  content: string;
  fit?: boolean;
  hideUse?: boolean;
  hideShare?: boolean;
  selectAble?: boolean;
  hideSocial?: boolean;
  selected?: boolean;
  onUse?: () => void;
  onCardClick?: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  index,
  title,
  description,
  content,
  slug,
  hideUse = false,
  hideShare = false,
  selected = false,
  selectAble = false,
  hideSocial = true,
  fit = false,
  onUse,
  onCardClick,
}) => {
  const componentName = "template-card";

  const handleShareTemplate = async (slug: string) => {
    //copy on clipboard the details page url
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/templates/${slug}`);
      message.success("Template link copied to clipboard");
    } catch (error) {
      console.error("Failed to copy template link to clipboard", error);
      message.error("Failed to copy template link to clipboard");
    }
  };

  const handleSocialShare = (platform: "facebook" | "twitter" | "linkedin" | "email") => {
    const shareUrl = `${window.location.origin}/templates/${slug}`;
    const shareText = `Check out this template: ${title}`;

    switch (platform) {
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          "_blank",
        );
        break;
      case "linkedin":
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank");
        break;
      case "email":
        window.open(`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareUrl)}`, "_blank");
        break;
    }
  };

  const bgClass =
    index % 3 === 0
      ? styles[`${componentName}__bg_1`]
      : index % 3 === 1
        ? styles[`${componentName}__bg_2`]
        : styles[`${componentName}__bg_3`];

  return (
    <Card
      onClick={onCardClick}
      className={
        fit
          ? selected
            ? `${styles[`${componentName}__fit`]} ${styles[`${componentName}__fit-selected`]}`
            : selectAble
              ? styles[`${componentName}__fit-selectable`]
              : styles[`${componentName}__fit`]
          : styles[componentName]
      }
    >
      <Flex vertical className={`${styles[`${componentName}__bg`]} ${bgClass}`} justify="space-between" align="center">
        <Flex justify="space-between" align="center">
          <Flex className={styles[`${componentName}__bg_sub`]}>
            <div>{content}</div>
          </Flex>
        </Flex>
      </Flex>
      <Flex vertical className={styles[`${componentName}__details`]}>
        <Flex vertical justify="space-between" flex={1}>
          <Flex align="baseline" vertical={selectAble}>
            <Flex align="baseline">
              {selectAble && (
                <Checkbox
                  checked={selected}
                  onChange={() => onCardClick}
                  style={{ marginRight: 10 }}
                  disabled={!selectAble}
                />
              )}
              <Flex vertical>
                <Typography.Text className={styles[`${componentName}__details-title`]}>{title}</Typography.Text>
                <Typography.Text>{description}</Typography.Text>
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={5} align="center" justify="space-between">
            {!hideShare && (
              <Button
                className={styles[`${componentName}__actions_sec`]}
                icon={<ShareAltOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShareTemplate(slug);
                }}
              >
                Share
              </Button>
            )}
            {!hideUse && (
              <Button type="primary" className={styles[`${componentName}__actions_pri`]} onClick={onUse}>
                Use Template
              </Button>
            )}
          </Flex>
          {!hideShare && !hideSocial && (
            <Flex gap={5} align="center" justify="space-between">
              <Button
                className={styles[`${componentName}__actions_social`]}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="22px" height="22px">
                    <linearGradient
                      id="Ld6sqrtcxMyckEl6xeDdMa"
                      x1="9.993"
                      x2="40.615"
                      y1="9.993"
                      y2="40.615"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0" stop-color="#2aa4f4" />
                      <stop offset="1" stop-color="#007ad9" />
                    </linearGradient>
                    <path
                      fill="url(#Ld6sqrtcxMyckEl6xeDdMa)"
                      d="M24,4C12.954,4,4,12.954,4,24s8.954,20,20,20s20-8.954,20-20S35.046,4,24,4z"
                    />
                    <path
                      fill="#fff"
                      d="M26.707,29.301h5.176l0.813-5.258h-5.989v-2.874c0-2.184,0.714-4.121,2.757-4.121h3.283V12.46 c-0.577-0.078-1.797-0.248-4.102-0.248c-4.814,0-7.636,2.542-7.636,8.334v3.498H16.06v5.258h4.948v14.452 C21.988,43.9,22.981,44,24,44c0.921,0,1.82-0.084,2.707-0.204V29.301z"
                    />
                  </svg>
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleSocialShare("facebook");
                }}
              ></Button>
              <Button
                className={styles[`${componentName}__actions_social`]}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20px" height="20px">
                    <path d="M 11 4 C 7.134 4 4 7.134 4 11 L 4 39 C 4 42.866 7.134 46 11 46 L 39 46 C 42.866 46 46 42.866 46 39 L 46 11 C 46 7.134 42.866 4 39 4 L 11 4 z M 13.085938 13 L 21.023438 13 L 26.660156 21.009766 L 33.5 13 L 36 13 L 27.789062 22.613281 L 37.914062 37 L 29.978516 37 L 23.4375 27.707031 L 15.5 37 L 13 37 L 22.308594 26.103516 L 13.085938 13 z M 16.914062 15 L 31.021484 35 L 34.085938 35 L 19.978516 15 L 16.914062 15 z" />
                  </svg>
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleSocialShare("twitter");
                }}
              ></Button>
              <Button
                className={styles[`${componentName}__actions_social`]}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="22px" height="22px">
                    <path
                      fill="#0078d4"
                      d="M42,37c0,2.762-2.238,5-5,5H11c-2.761,0-5-2.238-5-5V11c0-2.762,2.239-5,5-5h26c2.762,0,5,2.238,5,5 V37z"
                    />
                    <path
                      d="M30,37V26.901c0-1.689-0.819-2.698-2.192-2.698c-0.815,0-1.414,0.459-1.779,1.364 c-0.017,0.064-0.041,0.325-0.031,1.114L26,37h-7V18h7v1.061C27.022,18.356,28.275,18,29.738,18c4.547,0,7.261,3.093,7.261,8.274 L37,37H30z M11,37V18h3.457C12.454,18,11,16.528,11,14.499C11,12.472,12.478,11,14.514,11c2.012,0,3.445,1.431,3.486,3.479 C18,16.523,16.521,18,14.485,18H18v19H11z"
                      opacity=".05"
                    />
                    <path
                      d="M30.5,36.5v-9.599c0-1.973-1.031-3.198-2.692-3.198c-1.295,0-1.935,0.912-2.243,1.677 c-0.082,0.199-0.071,0.989-0.067,1.326L25.5,36.5h-6v-18h6v1.638c0.795-0.823,2.075-1.638,4.238-1.638 c4.233,0,6.761,2.906,6.761,7.774L36.5,36.5H30.5z M11.5,36.5v-18h6v18H11.5z M14.457,17.5c-1.713,0-2.957-1.262-2.957-3.001 c0-1.738,1.268-2.999,3.014-2.999c1.724,0,2.951,1.229,2.986,2.989c0,1.749-1.268,3.011-3.015,3.011H14.457z"
                      opacity=".07"
                    />
                    <path
                      fill="#fff"
                      d="M12,19h5v17h-5V19z M14.485,17h-0.028C12.965,17,12,15.888,12,14.499C12,13.08,12.995,12,14.514,12 c1.521,0,2.458,1.08,2.486,2.499C17,15.887,16.035,17,14.485,17z M36,36h-5v-9.099c0-2.198-1.225-3.698-3.192-3.698 c-1.501,0-2.313,1.012-2.707,1.99C24.957,25.543,25,26.511,25,27v9h-5V19h5v2.616C25.721,20.5,26.85,19,29.738,19 c3.578,0,6.261,2.25,6.261,7.274L36,36L36,36z"
                    />
                  </svg>
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleSocialShare("linkedin");
                }}
              ></Button>
              <Button
                className={styles[`${componentName}__actions_social`]}
                icon={<MailOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSocialShare("email");
                }}
              ></Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

TemplateCard.defaultProps = {
  fit: false,
  hideUse: false,
  hideShare: false,
  hideSocial: true,
};

export default TemplateCard;
